import { ActionBadge } from "components/risk/ActionBadge"
import { ACTION_BADGE_CONFIG, BLACKLIST_TYPE_BADGE, PRIORITY_BADGE, STATUS_BADGE } from "components/risk/constants"
import { Column } from "components/tables/DataTable"

import { AutoActionRule, BlacklistEntry, RecentAlert, ReviewDecision, ReviewItem } from "services/risk/types"

import dayjs from "libs/dayjs"

export const buildAlertColumns = (navigate: (to: string) => void): Column<RecentAlert>[] => [
  { header: "Action", render: (a) => <ActionBadge action={a.action} /> },
  {
    header: "Player",
    render: (a) => (
      <button className="link link-primary text-sm" onClick={() => navigate(`/user/${a.player_id}`)}>
        {a.player_username}
      </button>
    )
  },
  { header: "Rule", render: (a) => <>{a.rule_name || "-"}</> },
  { header: "Time", render: (a) => <>{dayjs(a.triggered_at).format("DD.MM.YYYY h:mm A")}</> }
]

export const buildAutoActionColumns = (handlers: {
  onToggle: (rule: AutoActionRule) => void
  onDelete: (rule: AutoActionRule) => void
  onEdit: (rule: AutoActionRule) => void
}): Column<AutoActionRule>[] => [
  {
    header: "Name",
    render: (rule) => (
      <div>
        <div className="font-medium">{rule.name}</div>
        {rule.description && <div className="text-xs text-gray-500 dark:text-gray-400">{rule.description}</div>}
      </div>
    )
  },
  {
    header: "Conditions",
    render: (rule) => (
      <div className="flex flex-wrap gap-1">
        {rule.conditions.map((c, i) => (
          <span key={i} className="badge badge-outline badge-sm">
            {c.factor} {c.operator} {c.level || c.severity}
          </span>
        ))}
      </div>
    )
  },
  {
    header: "Actions",
    render: (rule) => (
      <div className="flex flex-wrap gap-1">
        {rule.actions.map((a) => {
          const meta = ACTION_BADGE_CONFIG[a] || { label: a, cls: "badge-ghost" }
          return (
            <span key={a} className={`badge ${meta.cls} badge-sm`}>
              {meta.label}
            </span>
          )
        })}
      </div>
    )
  },
  { header: "Executions", render: (rule) => <>{rule.execution_count}</> },
  {
    header: "Last Run",
    render: (rule) => <>{rule.last_executed_at ? dayjs(rule.last_executed_at).format("YYYY-MM-DD HH:mm") : "Never"}</>
  },
  {
    header: "Enabled",
    render: (rule) => (
      <input
        type="checkbox"
        className="toggle toggle-sm toggle-success"
        checked={rule.is_active}
        onChange={() => handlers.onToggle(rule)}
      />
    )
  },
  {
    header: "",
    render: (rule) => (
      <div className="flex gap-1">
        <button className="btn btn-xs btn-ghost" onClick={() => handlers.onEdit(rule)}>
          Edit
        </button>
        <button className="btn btn-xs btn-ghost text-error" onClick={() => handlers.onDelete(rule)}>
          Delete
        </button>
      </div>
    )
  }
]

export const buildReviewColumns = (handlers: {
  navigate: (to: string) => void
  resolvingId: string | null
  onResolve: (id: string, decision: ReviewDecision) => void
}): Column<ReviewItem>[] => [
  {
    header: "Player",
    render: (item) => (
      <div>
        <button className="link link-hover font-medium" onClick={() => handlers.navigate(`/user/${item.user_id}`)}>
          {item.username}
        </button>
        <div className="text-xs text-gray-500 dark:text-gray-400">{item.email}</div>
      </div>
    )
  },
  {
    header: "Priority",
    render: (item) => <span className={`badge ${PRIORITY_BADGE[item.priority] || "badge-ghost"} badge-sm`}>{item.priority}</span>
  },
  {
    header: "Reason",
    render: (item) => (
      <div className="max-w-[300px] truncate text-sm" title={item.reason}>
        {item.reason}
      </div>
    )
  },
  {
    header: "Source",
    render: (item) => <span className="text-xs">{item.source_type === "AUTO_ACTION" ? "Auto" : "Manual"}</span>
  },
  {
    header: "Status",
    render: (item) => (
      <div>
        <span className={`badge ${STATUS_BADGE[item.status] || "badge-ghost"} badge-sm`}>{item.status}</span>
        {item.resolution && (
          <div className="text-xs mt-1 text-gray-500 dark:text-gray-400" title={item.resolution}>
            {item.resolution.length > 40 ? item.resolution.slice(0, 40) + "..." : item.resolution}
          </div>
        )}
      </div>
    )
  },
  { header: "Created", render: (item) => <>{dayjs(item.created_at).format("YYYY-MM-DD HH:mm")}</> },
  {
    header: "Actions",
    render: (item) =>
      item.status === "PENDING" ? (
        <div className="flex gap-1">
          <button
            className="btn btn-xs btn-success"
            disabled={handlers.resolvingId === item.id}
            onClick={() => handlers.onResolve(item.id, "REVIEWED")}
          >
            {handlers.resolvingId === item.id ? <span className="loading loading-spinner loading-xs" /> : "Review"}
          </button>
          <button
            className="btn btn-xs btn-ghost"
            disabled={handlers.resolvingId === item.id}
            onClick={() => handlers.onResolve(item.id, "DISMISSED")}
          >
            Dismiss
          </button>
        </div>
      ) : (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {item.reviewed_at ? dayjs(item.reviewed_at).format("MMM DD HH:mm") : "-"}
        </span>
      )
  }
]

export const buildBlacklistColumns = (handlers: {
  onEdit: (entry: BlacklistEntry) => void
  onToggle: (entry: BlacklistEntry) => void
  onDelete: (entry: BlacklistEntry) => void
}): Column<BlacklistEntry>[] => [
  {
    header: "Type",
    render: (entry) => <span className={`badge badge-sm ${BLACKLIST_TYPE_BADGE[entry.type] || "badge-ghost"}`}>{entry.type}</span>
  },
  { header: "Value", render: (entry) => <span className="font-mono text-sm">{entry.value}</span> },
  {
    header: "Reason",
    render: (entry) => <span className="max-w-xs truncate">{entry.reason || "—"}</span>
  },
  {
    header: "Status",
    render: (entry) => (
      <input
        type="checkbox"
        className="toggle toggle-sm toggle-success"
        checked={entry.is_active}
        onChange={() => handlers.onToggle(entry)}
      />
    )
  },
  {
    header: "Created",
    render: (entry) => <span className="text-xs opacity-60">{new Date(entry.created_at).toLocaleDateString()}</span>
  },
  {
    header: "",
    align: "right",
    render: (entry) => (
      <div className="flex gap-1 justify-end">
        <button className="btn btn-ghost btn-xs" onClick={() => handlers.onEdit(entry)}>
          Edit
        </button>
        <button className="btn btn-ghost btn-xs text-error" onClick={() => handlers.onDelete(entry)}>
          Delete
        </button>
      </div>
    )
  }
]
